import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FrontendApi, VerificationFlow } from '@ory/client';
import { from, of as observableOf } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as actions from './verification.actions';
import { auth as authSelectors } from '../auth.selectors';
import { State } from '../../reducers';

export interface KratosError {
  error: {
    id: string;
    code: number;
    status: string;
    reason: string;
    message: string;
  };
  redirect_browser_to?: string;
}

@Injectable()
export class AuthVerificationEffects {
  createVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCreateVerificationFlow),
      switchMap((action) =>
        from(this.kratos.createBrowserVerificationFlow()).pipe(
          map((res) => actions.SuccessGetVerificationFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailGetVerificationFlow({ error: err })))
        )
      )
    )
  );
  getVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestGetVerificationFlow),
      switchMap((action) =>
        from(this.kratos.getVerificationFlow({ id: action.id })).pipe(
          map((res) => actions.SuccessGetVerificationFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailGetVerificationFlow({ error: err })))
        )
      )
    )
  );

  updateVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateVerificationFlow),
      withLatestFrom(this.store.select(authSelectors.getVerificationFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos
            .updateVerificationFlow({
              flow: flow.id,
              updateVerificationFlowBody: {
                method: 'code',
                code: action.code,
                email: action.email,
                csrf_token: (<VerificationFlow>flow).ui.nodes.find(
                  (n) => n.attributes['name'] === 'csrf_token'
                ).attributes['value'],
              },
            })
            // we need the error payloads, and they get stripped away if we just catch the
            // observable error
            .catch((err) => {
              return err.response;
            })
        ).pipe(
          map((res) => {
            if (res.data.state === 'passed_challenge') {
              return actions.SuccessUpdateVerificationFlow({ response: res.data });
            } else {
              return actions.FailUpdateVerificationFlow({ response: res.data });
            }
          }),

          catchError((err) => {
            return observableOf(actions.FailUpdateVerificationFlow({ response: err }));
          })
        )
      )
    )
  );

  constructor(
    private store: Store<State>,
    private actions$: Actions,
    private kratos: FrontendApi
  ) {}
}
